import React, { useEffect, useState } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';

export default function AdminReports({ openModal }) {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchReports();
    }, []);

    const fetchReports = async () => {
        try {
            const snap = await getDocs(collection(db, "reports"));
            const rawReports = snap.docs.map(d => ({ id: d.id, ...d.data() }));

            // Aggregate reports by reportedUser
            const aggregated = {};

            for (const r of rawReports) {
                const uid = r.reportedUser;
                if (!aggregated[uid]) {
                    aggregated[uid] = {
                        uid,
                        count: 0,
                        reasons: [],
                        lastReported: r.timestamp
                    };
                }
                aggregated[uid].count++;
                if (r.reason) aggregated[uid].reasons.push(r.reason);
            }

            // Fetch Usernames for these UIDs
            const reportList = Object.values(aggregated);

            const enrichedList = await Promise.all(reportList.map(async (item) => {
                try {
                    const userSnap = await getDoc(doc(db, "users", item.uid));
                    if (userSnap.exists()) {
                        const uData = userSnap.data();
                        return {
                            ...item,
                            username: uData.username || "Unknown",
                            email: uData.email,
                            role: uData.role || 'user',
                            isBlocked: !!uData.isBlocked,
                            id: item.uid // Important for Modal to use 'id'
                        };
                    }
                } catch (e) {
                    console.error("Error fetching user details", e);
                }
                return { ...item, username: "Deleted/Unknown User", id: item.uid };
            }));

            // Sort by count desc
            enrichedList.sort((a, b) => b.count - a.count);

            setReports(enrichedList);
            setLoading(false);
        } catch (e) {
            console.error("Error fetching reports:", e);
            setLoading(false);
        }
    };

    const handleBlock = async (uid, currentStatus) => {
        try {
            await updateDoc(doc(db, "users", uid), {
                isBlocked: !currentStatus
            });
            // Update Local State
            setReports(prev => prev.map(r => r.uid === uid ? { ...r, isBlocked: !currentStatus } : r));
        } catch (e) {
            alert("Failed to update status: " + e.message);
        }
    };

    const handleDelete = async (uid) => {
        if (!window.confirm("Are you sure? This deletes the user account.")) return;
        try {
            await deleteDoc(doc(db, "users", uid));
            // Remove from list or mark as deleted? 
            // In reports, we might still want to see the report history, but functionally the user is gone.
            // Let's remove them from the list to show action is done.
            setReports(prev => prev.filter(r => r.uid !== uid));
        } catch (e) {
            alert("Failed to delete user: " + e.message);
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500">Loading reports...</div>;

    return (
        <div className="admin-reports p-6 max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white flex items-center gap-3">
                <span className="bg-red-100 text-red-600 p-2 rounded-lg">🚩</span>
                Reported Users Management
            </h2>

            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-50 dark:bg-slate-700/50 text-gray-600 dark:text-gray-300 uppercase text-xs font-semibold tracking-wider">
                            <tr>
                                <th className="p-5 border-b dark:border-gray-700">Reported User</th>
                                <th className="p-5 border-b dark:border-gray-700 text-center">Count</th>
                                <th className="p-5 border-b dark:border-gray-700">Role</th>
                                <th className="p-5 border-b dark:border-gray-700">Status</th>
                                <th className="p-5 border-b dark:border-gray-700">Reasons</th>
                                <th className="p-5 border-b dark:border-gray-700 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {reports.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="p-8 text-center text-gray-500">
                                        <div className="flex flex-col items-center">
                                            <span className="text-4xl mb-2">✨</span>
                                            <p>No reported users found. Provide a safe community!</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                reports.map((r) => (
                                    <tr key={r.uid} className="hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors group">
                                        <td className="p-5">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg shadow-sm">
                                                    {r.username[0]?.toUpperCase()}
                                                </div>
                                                <div>
                                                    <div className="font-bold text-gray-900 dark:text-white">{r.username}</div>
                                                    <div className="text-xs text-gray-500">{r.email}</div>
                                                    <div className="text-[10px] text-gray-400 font-mono mt-1 opacity-0 group-hover:opacity-100 transition-opacity select-all">{r.uid}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-5 text-center">
                                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm shadow-sm ${r.count > 3 ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-orange-600'}`}>
                                                {r.count}
                                            </span>
                                        </td>
                                        <td className="p-5">
                                            <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${r.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300'}`}>
                                                {r.role}
                                            </span>
                                        </td>
                                        <td className="p-5">
                                            {r.isBlocked ? (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                                                    🚫 Blocked
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                                    ✅ Active
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-5">
                                            <div className="text-sm text-gray-600 dark:text-gray-300 max-w-[150px] truncate" title={r.reasons.join(", ")}>
                                                {r.reasons.join(", ")}
                                            </div>
                                        </td>
                                        <td className="p-5 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => openModal(r)}
                                                    className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg dark:text-indigo-400 dark:hover:bg-white/5 transition-colors"
                                                    title="Edit User"
                                                >
                                                    ✏️
                                                </button>
                                                <button
                                                    onClick={() => handleBlock(r.uid, r.isBlocked)}
                                                    className={`p-2 rounded-lg transition-colors ${r.isBlocked ? 'text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-white/5' : 'text-orange-500 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-white/5'}`}
                                                    title={r.isBlocked ? "Unblock User" : "Block User"}
                                                >
                                                    {r.isBlocked ? '🔓' : '🚫'}
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(r.uid)}
                                                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg dark:hover:bg-white/5 transition-colors"
                                                    title="Delete User"
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
